import { Box } from '../Box'
import type { useDimensionsProps } from '../../../hooks/useDimensions'
import type { BoxProps } from '../Box'
import { useState, useEffect, useRef, useCallback, forwardRef } from 'react'
import { getHostNode } from '../../../utils'
import { createPortal } from 'react-dom'

export type ModalProps = {
  children: React.ReactNode,
  className?: any,
  contentClass?: any,
  rootClass?: string,
  blur?: boolean,
  transparent?: boolean,
  active?: boolean,
  eager?: boolean,
  align?: 'start' | 'center' | 'end',
  permanent: boolean,
  onClickOutside?: (e: Event) => void,
  onClick?: (e: Event) => void
} & useDimensionsProps & BoxProps

function keepPermanent () {
  let element: HTMLElement | undefined
  let parent: HTMLElement | undefined
  let interval: NodeJS.Timeout | undefined
  return (node: HTMLElement | null | undefined, active: boolean) => {
    clearInterval(interval)
    if (active) {
      interval = setInterval(() => {
        if (node && node.parentNode) {
          element = node
          parent = node.parentNode as HTMLElement
        } else if (node && parent && element) {
          parent.appendChild(element)
        }
      }, 1000)
    }
    return () => clearInterval(interval)
  }
}

function keepBlocker () {
  let interval: NodeJS.Timeout | undefined
  return (node: HTMLElement | null | undefined, active: boolean) => {
    clearInterval(interval)
    if (active) {
      interval = setInterval(() => {
        if (!node) return
        // Check display
        const cs = window.getComputedStyle(node)
        if (cs.display !== 'flex') {
          node.style.display = 'flex'
        }
        // Check zIndex
        if (parseInt(cs.zIndex) < 6000) {
          node.style.zIndex = '6000'
        }
        // Check pointer-events
        if (cs.pointerEvents !== 'auto') {
          node.style.pointerEvents = 'auto'
        }
        // Check dimensions
        const box = node.getBoundingClientRect()
        if (
          box.top > 0 ||
          box.left > 0 ||
          box.width < document.body.offsetWidth ||
          box.height < window.innerHeight
        ) {
          node.style.position = 'fixed'
          node.style.top = node.style.left = node.style.bottom = node.style.right = '0'
          node.style.width = node.style.height = 'auto'
          node.style.margin = '0'
          node.style.transform = 'none'
        }
      }, 1000)
    }
    return () => clearInterval(interval)
  }
}

export const Modal = forwardRef(function Modal ({
  className,
  contentClass,
  rootClass,
  children,
  blur,
  width,
  maxWidth,
  minWidth,
  height,
  maxHeight,
  minHeight,
  align = 'center',
  active = true,
  eager = false,
  transparent = false,
  permanent = false,
  onClick,
  onClickOutside,
  theme = 'light',
  ...props
} : ModalProps, ref) {

  const rootRef = useRef<HTMLDivElement | null>(null)
  const hostRef = useRef<HTMLElement | null>(null)
  const [modalRef, setModalRef] = useState<HTMLDivElement | null>(null)

  const getRootRef = (node: HTMLDivElement | null) => {
    try {
      rootRef.current = node;
      hostRef.current = getHostNode(node as HTMLDivElement)
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    } catch {}
  }

  const [isHide, setIsHide] = useState<boolean>(!active)
  const [isActive, setIsActive] = useState<boolean>(false)
  const [AFRef, setAFRef] = useState<number>(0)
  const [TRef, setTRef] = useState<number>(0)

  function onClickHandler (e: Event) {
    e.stopPropagation()
    onClick && onClick(e)
  }

  useEffect(() => {
    if (active) {
      setIsHide(false)
    } else {
      setIsActive(false)
    }
  }, [active])

  useEffect(() => {
    if (!isHide) {
      clearInterval(TRef)
      window.cancelAnimationFrame(AFRef)
      setAFRef(window.requestAnimationFrame(() => setIsActive(true)))
    }
  }, [isHide])

  useEffect(() => {
    if (!isActive) {
      clearInterval(TRef)
      window.cancelAnimationFrame(AFRef)
      setTRef(window.setTimeout(() => setIsHide(true), 400))
    }
  }, [isActive])

  // Keep permanent

  const KEEP_BLOKER = useCallback(keepBlocker(), [])
  const KEEP = {
    root: useCallback(keepPermanent(), []),
    modal: useCallback(keepPermanent(), [])
  }

  useEffect(() => {
    return KEEP.root(rootRef.current, permanent && active)
  }, [KEEP.root, permanent, active, rootRef])

  useEffect(() => {
    return KEEP.modal(modalRef, permanent && active)
  }, [KEEP.modal, permanent, active, modalRef])

  useEffect(() => {
    return KEEP_BLOKER(modalRef, permanent && active)
  }, [KEEP_BLOKER, permanent, active, modalRef])

  // Render

  const element = (
    <div
      ref={getRootRef}
      className={rootClass}
    >
      {(eager || !isHide) && (
        <Box
          ref={setModalRef}
          onClick={onClickOutside}
          theme={theme}
          style={{
            display: eager
              ? isHide ? 'none' : 'flex'
              : ''
          }}
          className={[
            'topbar-modal',
            isActive && 'active',
            transparent && 'transparent',
            blur && 'blur',
            `align-${align}`,
            className
          ]}
        >
          <Box
            className={['topbar-modal-content', contentClass]}
            maxWidth={maxWidth}
            minWidth={minWidth}
            width={width}
            maxHeight={maxHeight}
            minHeight={minHeight}
            height={height}
            onClick={onClickHandler}
            {...props}
          >
            {children}
          </Box>
        </Box>
      )}
    </div>
  )

  if (hostRef.current) return createPortal(element, hostRef.current)
  return element
})